import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  InfiniteTable,
  InfiniteTableCell,
  InfiniteTableColumns,
  InfiniteTableComponents,
  InfiniteTableRow,
  InfiniteTableSortBy,
  Input,
  Modal,
  ModalHeader,
  ModalMain,
  Stack,
  Text,
} from '@sixfold/common-ui';
import { DateTime } from 'luxon';
import * as React from 'react';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';

import { adminApiMetricsMessages, companyApiMetricsMessages } from '../../company/graphql';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { toDateTime } from '../../lib/date';
import { AdminApiMetricsMessagesQuery, CompanyApiMetricsMessagesQuery, SortDirection } from '../../lib/graphql';
import { NetworkStatus } from '../../lib/loader';
import { useDebounce } from '../../lib/util/debounce';

import styles from './api_call_details_modal.module.css';

const API_METRICS_PAGE_SIZE = 50;

const HTTP_STATUS_CODE = {
  SUCCESS_MIN: 200,
  SUCCESS_MAX: 299,
  CLIENT_ERROR_MIN: 400,
} as const;

const COLUMN_COUNT = 5;

const safeParseStatusCode = (value: string | null | undefined, fallback: number = 0): number => {
  if (!value) {
    return fallback;
  }
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? fallback : parsed;
};

export interface ApiCallDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  endpointId: string;
  endpointName: string;
  integrationIdentifier: string;
  date: string;
  statusCode: string;
  apiCallsCount: number;
  useAdminQuery?: boolean;
}

interface LogEntryRow {
  id: string;
  type: 'entry' | 'expandedContent';
  payload: string | null;
  parsedPayload: unknown;
  errorMessage: string | null;
  timestamp: string;
  statusCode: number;
  requestUrl: string;
  rowIndex: number;
  actions: undefined;
}

type TableContext = {
  expandedEntries: Set<string>;
  toggleEntry: (entryKey: string) => void;
  collapsedJsonEntries: Set<string>;
  toggleJsonCollapse: (entryKey: string) => void;
  [key: string]: unknown;
};

const getStatusClassName = (statusCode: number): string => {
  if (statusCode >= HTTP_STATUS_CODE.SUCCESS_MIN && statusCode <= HTTP_STATUS_CODE.SUCCESS_MAX) {
    return styles.statusCodeSuccess;
  }
  if (statusCode >= HTTP_STATUS_CODE.CLIENT_ERROR_MIN) {
    return styles.statusCodeError;
  }
  return styles.statusCodeDefault;
};

export const ApiCallDetailsModal: React.FC<ApiCallDetailsModalProps> = ({
  isOpen,
  onClose,
  companyId,
  endpointId,
  endpointName,
  integrationIdentifier,
  date,
  statusCode,
  apiCallsCount,
  useAdminQuery = true,
}) => {
  const [expandedEntries, setExpandedEntries] = React.useState<Set<string>>(new Set());
  const [collapsedJsonEntries, setCollapsedJsonEntries] = React.useState<Set<string>>(new Set());
  const [searchInput, setSearchInput] = React.useState('');
  const [startTimeInput, setStartTimeInput] = React.useState('');
  const [endTimeInput, setEndTimeInput] = React.useState('');
  const [sortBy, setSortBy] = React.useState<InfiniteTableSortBy<keyof LogEntryRow>>({
    columnId: 'timestamp',
    order: 'DESC',
  });

  const timeSortDirection = sortBy.order === 'DESC' ? SortDirection.DESCENDING : SortDirection.ASCENDING;

  const debouncedSearch = useDebounce(searchInput, 500);

  const toggleEntry = React.useCallback((entryKey: string) => {
    setExpandedEntries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(entryKey)) {
        newSet.delete(entryKey);
      } else {
        newSet.add(entryKey);
      }
      return newSet;
    });
  }, []);

  const toggleJsonCollapse = React.useCallback((entryKey: string) => {
    setCollapsedJsonEntries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(entryKey)) {
        newSet.delete(entryKey);
      } else {
        newSet.add(entryKey);
      }
      return newSet;
    });
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      setExpandedEntries(new Set());
      setCollapsedJsonEntries(new Set());
      setSearchInput('');
      setStartTimeInput('');
      setEndTimeInput('');
      setSortBy({ columnId: 'timestamp', order: 'DESC' });
    }
  }, [isOpen, endpointId, date, statusCode]);

  const dateTime = DateTime.fromISO(date, { zone: 'UTC' });

  const startDate = React.useMemo(() => {
    const dt = DateTime.fromISO(date, { zone: 'UTC' });
    if (!dt.isValid) return null;
    const dayStart = dt.startOf('day');
    if (startTimeInput.match(/^\d{2}:\d{2}$/)) {
      const [hours, minutes] = startTimeInput.split(':').map(Number);
      return dayStart.set({ hour: hours, minute: minutes }).toISO();
    }
    return dayStart.toISO();
  }, [date, startTimeInput]);

  const endDate = React.useMemo(() => {
    const dt = DateTime.fromISO(date, { zone: 'UTC' });
    if (!dt.isValid) return null;
    const dayEnd = dt.endOf('day');
    if (endTimeInput.match(/^\d{2}:\d{2}$/)) {
      const [hours, minutes] = endTimeInput.split(':').map(Number);
      return dt.startOf('day').set({ hour: hours, minute: minutes, second: 59, millisecond: 999 }).toISO();
    }
    return dayEnd.toISO();
  }, [date, endTimeInput]);

  const query = useAdminQuery ? adminApiMetricsMessages : companyApiMetricsMessages;

  const result = useQuery(query, {
    variables: {
      companyId,
      endpointId,
      statusCode,
      startDate,
      endDate,
      first: API_METRICS_PAGE_SIZE,
      searchTerm: debouncedSearch || undefined,
      sortDirection: timeSortDirection,
    },
    skip: !isOpen || !dateTime.isValid,
    fetchPolicy: 'cache-and-network',
  });

  const { loading, error, data, networkStatus, fetchMore } = result;
  const isInitialLoading = loading && networkStatus !== NetworkStatus.fetchMore;

  const messagesConnection = React.useMemo(() => {
    if (!data) return null;
    if (useAdminQuery) {
      return (data as AdminApiMetricsMessagesQuery)?.apiMetricsMessages;
    }
    return (data as CompanyApiMetricsMessagesQuery)?.company?.apiMetricsMessages;
  }, [data, useAdminQuery]);

  const hasNextPage = messagesConnection?.pageInfo?.hasNextPage ?? false;

  const [loadMoreError, setLoadMoreError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      setLoadMoreError(null);
    }
  }, [isOpen, endpointId, date, statusCode, sortBy, startTimeInput, endTimeInput]);

  const loadMore = React.useCallback(async () => {
    if (!messagesConnection?.pageInfo?.hasNextPage) return;
    try {
      await fetchMore({
        variables: {
          first: API_METRICS_PAGE_SIZE,
          cursor: messagesConnection.pageInfo.endCursor ?? undefined,
        },
      });
      setLoadMoreError(null);
    } catch (err) {
      setLoadMoreError(err instanceof Error ? err : new Error('Failed to load more data'));
      console.error('Failed to load more API metrics messages:', err);
    }
  }, [messagesConnection, fetchMore]);

  const logEntries: LogEntryRow[] = React.useMemo(() => {
    const allEdges = messagesConnection?.edges || [];
    const entries: LogEntryRow[] = [];

    allEdges.forEach((edge, index) => {
      if (!edge || !edge.node || !edge.node.timestamp) return;

      const msg = edge.node;
      const payload = msg.requestPayload ?? null;
      let parsedPayload: unknown = null;
      if (payload !== null) {
        try {
          parsedPayload = JSON.parse(payload);
        } catch {
          parsedPayload = payload;
        }
      }

      const timestamp = msg.timestamp as string;
      const msgStatusCode = safeParseStatusCode(msg.statusCode, safeParseStatusCode(statusCode, 0));
      const rowId = `${timestamp}-${msgStatusCode}-${msg.requestUrl || ''}-${index}`;

      const row: LogEntryRow = {
        id: rowId,
        type: 'entry',
        payload,
        parsedPayload,
        errorMessage: msg.errorMessage,
        timestamp,
        statusCode: msgStatusCode,
        requestUrl: msg.requestUrl || '',
        rowIndex: index,
        actions: undefined,
      };

      entries.push(row);

      if (expandedEntries.has(rowId)) {
        entries.push({
          ...row,
          id: `${rowId}-expanded`,
          type: 'expandedContent',
        });
      }
    });

    return entries;
  }, [messagesConnection, statusCode, expandedEntries]);

  const columns: InfiniteTableColumns<LogEntryRow, TableContext> = React.useMemo(
    () => [
      {
        id: 'rowIndex',
        width: 5,
        title: '#',
        cellContent: ({ row }) => <Text size="small">{row.rowIndex + 1}</Text>,
      },
      {
        id: 'statusCode',
        width: 8,
        align: 'end' as const,
        title: 'Status',
        cellContent: ({ row }) => (
          <span className={`${styles.statusCode} ${getStatusClassName(row.statusCode)}`}>{row.statusCode}</span>
        ),
      },
      {
        id: 'requestUrl',
        width: 57,
        title: 'URL',
        cellContent: ({ row }) => <span className={styles.urlText}>{row.requestUrl}</span>,
      },
      {
        id: 'timestamp',
        width: 22,
        align: 'end' as const,
        isUserSortable: true,
        title: 'Time (UTC)',
        cellContent: ({ row }) => (
          <span className={styles.monospaceText}>
            {toDateTime(new Date(row.timestamp)).setZone('UTC').toFormat('HH:mm:ss')}
          </span>
        ),
      },
      {
        id: 'actions',
        width: 8,
        align: 'end' as const,
        title: undefined,
        cellContent: ({ row, context }) => {
          if (row.type === 'expandedContent') return <></>;
          const hasExpandableContent =
            row.statusCode >= HTTP_STATUS_CODE.CLIENT_ERROR_MIN || row.errorMessage !== null || row.payload !== null;
          if (!hasExpandableContent) return <></>;
          const isExpanded = context.expandedEntries.has(row.id);
          return (
            <Button
              hideLabel
              iconStart={isExpanded ? 'small-chevron-down' : 'small-chevron-right'}
              size="small"
              kind="ghost"
              onClick={(e) => {
                e.stopPropagation();
                context.toggleEntry(row.id);
              }}>
              {isExpanded ? 'Collapse' : 'Expand'}
            </Button>
          );
        },
      },
    ],
    [],
  );

  const tableContext: TableContext = React.useMemo(
    () => ({ expandedEntries, toggleEntry, collapsedJsonEntries, toggleJsonCollapse }),
    [expandedEntries, toggleEntry, collapsedJsonEntries, toggleJsonCollapse],
  );

  const components = React.useMemo(
    (): InfiniteTableComponents<LogEntryRow, TableContext> => ({
      Row: ({ item, context, ...rest }) => {
        if (item.type === 'expandedContent') {
          const entryKey = item.id.replace(/-expanded$/, '');
          const jsonCollapsed = context?.collapsedJsonEntries.has(entryKey) ?? false;

          return (
            <InfiniteTableRow item={item} {...rest}>
              <InfiniteTableCell colSpan={COLUMN_COUNT}>
                <Stack direction="column" gap="medium" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                  {item.errorMessage && (
                    <Stack direction="column" gap="xsmall">
                      <Text size="small" weight="bold">
                        Error
                      </Text>
                      <Box className={styles.errorBox}>
                        <span className={styles.monospaceText}>{item.errorMessage}</span>
                      </Box>
                    </Stack>
                  )}

                  {item.parsedPayload !== null && (
                    <Stack direction="column" gap="xsmall">
                      <Stack direction="row" gap="small" alignItems="center">
                        <Text size="small" weight="bold">
                          Payload
                        </Text>
                        <Button
                          size="xsmall"
                          kind="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            context?.toggleJsonCollapse(entryKey);
                          }}>
                          {jsonCollapsed ? 'Expand All' : 'Collapse All'}
                        </Button>
                      </Stack>
                      <Box className={styles.payloadBox}>
                        {typeof item.parsedPayload === 'object' && item.parsedPayload !== null ? (
                          <JsonView
                            key={jsonCollapsed ? 'collapsed' : 'expanded'}
                            src={item.parsedPayload as object}
                            collapsed={jsonCollapsed ? 1 : false}
                            enableClipboard={true}
                          />
                        ) : (
                          <pre className={styles.payloadPre}>{String(item.parsedPayload)}</pre>
                        )}
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </InfiniteTableCell>
            </InfiniteTableRow>
          );
        }

        const hasExpandableContent =
          item.statusCode >= HTTP_STATUS_CODE.CLIENT_ERROR_MIN || item.errorMessage !== null || item.payload !== null;
        const isExpanded = context?.expandedEntries.has(item.id) ?? false;

        return (
          <InfiniteTableRow
            className={isExpanded ? styles.logEntryRowExpanded : undefined}
            onClick={hasExpandableContent ? () => context?.toggleEntry(item.id) : undefined}
            style={hasExpandableContent ? { cursor: 'pointer' } : undefined}
            item={item}
            {...rest}
          />
        );
      },
    }),
    [],
  );

  const onSortByChange = React.useCallback((newSortBy: InfiniteTableSortBy<keyof LogEntryRow>) => {
    setSortBy(newSortBy);
  }, []);

  if (!isOpen) {
    return null;
  }

  if (!dateTime.isValid) {
    console.error('Invalid date provided to ApiCallDetailsModal:', date);
    return (
      <Modal trigger={<span className={styles.hiddenTrigger} />} open={isOpen} onToggle={onClose} size="xlarge">
        <ModalHeader title="API Call History" />
        <ModalMain>
          <Box className={styles.contentPadding}>
            <Box className={styles.errorMessage}>Invalid date format</Box>
          </Box>
        </ModalMain>
      </Modal>
    );
  }

  return (
    <Modal trigger={<span className={styles.hiddenTrigger} />} open={isOpen} onToggle={onClose} size="xlarge">
      <ModalHeader title="API Call History" />
      <Box className={styles.headerSection}>
        <Box className={styles.headerInfo}>
          <Stack direction="column" gap="small">
            <Stack direction="row" gap="large" wrap={true}>
              <Stack direction="column" gap="xsmall">
                <span className={styles.label}>Endpoint</span>
                <span className={styles.valueText}>{endpointName}</span>
              </Stack>

              <Stack direction="column" gap="xsmall">
                <span className={styles.label}>Source</span>
                <span className={styles.valueText}>{integrationIdentifier}</span>
              </Stack>

              <Stack direction="column" gap="xsmall">
                <span className={styles.label}>Date</span>
                <span className={styles.valueText}>
                  <FormattedDate
                    date={new Date(date)}
                    showDate
                    showTime={false}
                    showTimezone={false}
                    propsTimezone="UTC"
                  />
                </span>
              </Stack>

              <Stack direction="column" gap="xsmall">
                <span className={styles.label}>Total Calls</span>
                <span className={styles.valueTextBold}>{apiCallsCount}</span>
              </Stack>
            </Stack>

            <Stack direction="row" gap="medium" wrap={true} alignItems="end">
              <Stack direction="column" gap="xsmall" style={{ flex: 1, minWidth: 200 }}>
                <Input
                  label="Search"
                  name="search"
                  value={searchInput}
                  onChange={({ value }) => setSearchInput(value)}
                  placeholder="e.g. transport reference"
                  size="small"
                />
              </Stack>
              <Stack direction="column" gap="xsmall">
                <Input
                  label="From (UTC)"
                  name="startTime"
                  type="time"
                  value={startTimeInput}
                  onChange={({ value }) => setStartTimeInput(value)}
                  clearable={startTimeInput !== ''}
                  onClear={() => setStartTimeInput('')}
                  size="small"
                  maxWidth={130}
                />
              </Stack>
              <Stack direction="column" gap="xsmall">
                <Input
                  label="To (UTC)"
                  name="endTime"
                  type="time"
                  value={endTimeInput}
                  onChange={({ value }) => setEndTimeInput(value)}
                  clearable={endTimeInput !== ''}
                  onClear={() => setEndTimeInput('')}
                  size="small"
                  maxWidth={130}
                />
              </Stack>
            </Stack>

            <Stack direction="column" gap="xsmall">
              <span className={styles.noteText}>
                Note: There is a delay in syncing detailed logs. Recent logs from today may not be visible yet.
              </span>
              <span className={styles.noteText}>
                Payloads are always logged for error responses (4xx/5xx). For successful responses, payloads are only
                logged for this company if the &quot;Log successful API payloads&quot; feature flag is enabled.
              </span>
            </Stack>
          </Stack>
        </Box>
      </Box>
      <ModalMain>
        {isInitialLoading && <Box className={styles.stateMessage}>Loading API call details...</Box>}

        {!isInitialLoading && error && <Box className={styles.errorMessage}>Error loading data: {error.message}</Box>}

        {!isInitialLoading && !error && logEntries.length === 0 && (
          <Box className={styles.stateMessage}>No log entries found for this date and status</Box>
        )}

        {!isInitialLoading && !error && logEntries.length > 0 && (
          <>
            <div className={styles.tableContainer}>
              <InfiniteTable
                data={logEntries}
                columns={columns}
                context={tableContext}
                loadMoreData={hasNextPage ? loadMore : undefined}
                sortBy={sortBy}
                onSortByChange={onSortByChange}
                components={components}
              />
            </div>
            {loadMoreError && (
              <Box className={styles.contentPadding}>
                <Box className={styles.errorMessage}>Error loading more data: {loadMoreError.message}</Box>
              </Box>
            )}
          </>
        )}
      </ModalMain>
    </Modal>
  );
};
